'use client'
import React from 'react'

import type { Page } from '@/payload-types'

import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import { RichText } from '@/components/RichText'
import { Logo } from '@/components/brand/Logo'

/**
 * Первый экран: фото или видео на весь экран, текст внизу слева, солнце
 * как графика справа. Видео (если загружено в медиатеку) играет без звука
 * по кругу; для медленных сетей достаточно фото — компонент Media сам
 * подставит нужный тег по mime-типу файла.
 */
export const HighImpactHero: React.FC<Page['hero']> = ({ links, media, richText }) => {
  return (
    <section className="relative isolate -mt-16 md:-mt-20 min-h-[92svh] flex items-end overflow-hidden bg-ink text-sand">
      {media && typeof media === 'object' && (
        <Media
          fill
          priority
          resource={media}
          imgClassName="object-cover"
          videoClassName="absolute inset-0 h-full w-full object-cover"
          className="absolute inset-0 -z-20"
        />
      )}
      {/* градиент, чтобы текст читался поверх любого кадра */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-ink/85 via-ink/30 to-ink/10" aria-hidden />

      {/* знак клуба поверх кадра: солнце медленно крутится, надпись стоит ровно */}
      <div className="pointer-events-none absolute right-4 top-20 flex w-[30vw] max-w-[190px] flex-col items-center gap-2 md:right-[6%] md:top-24 md:max-w-[240px]">
        <Logo
          variant="sun"
          title=""
          aria-hidden
          className="h-auto w-[78%] text-sun/90 animate-[spin_120s_linear_infinite]"
        />
        <Logo variant="wordmark" title="UtroClub" className="h-auto w-full text-sand drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]" />
      </div>

      <div className="container relative pb-14 pt-40 md:pb-20">
        <div className="max-w-2xl [&_h1]:font-display [&_h1]:text-[clamp(1.75rem,3.4vw,3rem)] [&_h1]:leading-[0.95] [&_h1]:font-900 [&_h1]:text-sand [&_p]:text-lg [&_p]:md:text-xl [&_p]:text-sand/85 [&_p]:max-w-xl [&_strong]:text-sun">
          {richText && <RichText className="mb-8" data={richText} enableGutter={false} enableProse={false} />}
          {Array.isArray(links) && links.length > 0 && (
            <ul className="flex flex-wrap gap-3">
              {links.map(({ link }, i) => (
                <li key={i}>
                  <CMSLink
                    {...link}
                    size="lg"
                    // контурная кнопка на тёмном фото — светлая рамка вместо чёрной
                    className={
                      link.appearance === 'outline'
                        ? 'border-white/80 bg-ink/35 text-white backdrop-blur-sm hover:bg-sand hover:text-ink'
                        : undefined
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}
